import * as React from "react";
import { Icon } from "@amzn/awsui-components-react";
import { AGENT_CHAT_PANEL_COLLAPSED_WIDTH_PX, getAgentDisplayName, parseAgentOrigin } from "../../utils/agent-chat-utils";
import { AppContext } from "../../setup/context";
import { Preference } from "../../setup/preference";
import "../../styles/component/quick-agent-chat-panel/QuickAgentChatPanel.scss";

const DEFAULT_WIDTH = 450;
const MIN_WIDTH = 300;
/** Resize cap off the search page, same as the lab-off panel. */
const DEFAULT_MAX_WIDTH_RATIO = 0.5;
/** On the search results page the chat takes 70% and the results 30%; it is also the resize cap there. */
const SEARCH_WIDTH_RATIO = 0.7;

interface QuickAgentChatPanelV2Props {
    agentUrl: string;
    initialQuery?: string;
    defaultCollapsed?: boolean;
    /** True while the user is on the search results page: the panel takes 70% of the viewport. */
    searchLayout?: boolean;
}

const getSearchWidth = () => Math.round(window.innerWidth * SEARCH_WIDTH_RATIO);

export const QuickAgentChatPanelV2: React.FC<QuickAgentChatPanelV2Props> = ({ agentUrl, initialQuery, defaultCollapsed = true, searchLayout = false }) => {
    const [width, setWidth] = React.useState(() => searchLayout ? getSearchWidth() : DEFAULT_WIDTH);
    const [collapsed, setCollapsed] = React.useState(defaultCollapsed);
    // Desktop only: the panel covers the whole content area; its previous width is kept for the way back.
    const [fullScreen, setFullScreen] = React.useState(false);
    const [isResizing, setIsResizing] = React.useState(false);
    const isResizingRef = React.useRef(false);
    const previousWidth = React.useRef(DEFAULT_WIDTH);
    const listenersRef = React.useRef<{ move: (e: MouseEvent) => void; up: () => void } | null>(null);

    const agentOrigin = React.useMemo(() => parseAgentOrigin(agentUrl), [agentUrl]);
    const context = React.useContext(AppContext);
    const [agentName] = React.useState(() => getAgentDisplayName(context.hasLab(Preference.Lab.AgenticUI)));

    // Clean up drag listeners on unmount
    React.useEffect(() => {
        return () => {
            if (listenersRef.current) {
                document.removeEventListener("mousemove", listenersRef.current.move);
                document.removeEventListener("mouseup", listenersRef.current.up);
            }
        };
    }, []);

    // Entering the search page widens the panel to 70%; leaving it returns to the default width. The
    // saved pre-collapse width follows, so a panel collapsed on search reopens at the new layout's width.
    // Either way full screen ends, so a page opened from the results is not hidden behind the chat.
    // The open/collapsed state is left to the user.
    React.useEffect(() => {
        const layoutWidth = searchLayout ? getSearchWidth() : DEFAULT_WIDTH;
        setWidth(layoutWidth);
        previousWidth.current = layoutWidth;
        setFullScreen(false);
    }, [searchLayout]);

    // Sync the CSS variable with the current panel width
    React.useEffect(() => {
        if (!agentOrigin) {
            document.documentElement.style.removeProperty("--qa-panel-width");
            return;
        }
        const panelWidth = collapsed ? AGENT_CHAT_PANEL_COLLAPSED_WIDTH_PX : width;
        document.documentElement.style.setProperty("--qa-panel-width", `${panelWidth}px`);
        return () => {
            document.documentElement.style.removeProperty("--qa-panel-width");
        };
    }, [width, collapsed, agentOrigin]);

    if (!agentOrigin) {
        return null;
    }

    const iframeSrc = initialQuery
        ? `${agentUrl}?initialPrompt=${encodeURIComponent(initialQuery)}&showInitialPromptMessage=false`
        : agentUrl;

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        isResizingRef.current = true;
        setIsResizing(true);

        const maxWidthRatio = searchLayout ? SEARCH_WIDTH_RATIO : DEFAULT_MAX_WIDTH_RATIO;
        const onMouseMove = (e: MouseEvent) => {
            if (!isResizingRef.current) return;
            const maxWidth = window.innerWidth * maxWidthRatio;
            const newWidth = Math.min(Math.max(MIN_WIDTH, window.innerWidth - e.clientX), maxWidth);
            setWidth(newWidth);
        };

        const onMouseUp = () => {
            isResizingRef.current = false;
            setIsResizing(false);
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
            listenersRef.current = null;
        };

        listenersRef.current = { move: onMouseMove, up: onMouseUp };
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    };

    const handleCollapse = () => {
        previousWidth.current = width;
        setFullScreen(false);
        setCollapsed(true);
    };

    const handleExpand = () => {
        setCollapsed(false);
        setWidth(previousWidth.current || DEFAULT_WIDTH);
    };

    return (
        <>
            {/* Collapsed vertical tab - always in DOM, shown/hidden via display */}
            <div
                className="qa-chat-panel-collapsed"
                onClick={handleExpand}
                aria-label="Expand chat panel"
                style={{ display: collapsed ? undefined : "none" }}
            >
                <svg className="qa-collapsed-icon" viewBox="0 0 48 48" fill="none" width="28" height="28">
                    <path d="M24 6 C26 16, 32 22, 42 24 C32 26, 26 32, 24 42 C22 32, 16 26, 6 24 C16 22, 22 16, 24 6Z" fill="none" stroke="url(#qa-sparkle-gradient)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    <defs><linearGradient id="qa-sparkle-gradient" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style={{stopColor: "#7c3aed"}}/><stop offset="100%" style={{stopColor: "#e84393"}}/></linearGradient></defs>
                </svg>
                <span className="qa-collapsed-label">{agentName}</span>
            </div>

            {/* Expanded panel - always in DOM, shown/hidden via display */}
            <div
                className={`qa-chat-panel pf-chat-panel${isResizing ? " qa-chat-panel--resizing" : ""}${fullScreen ? " qa-chat-panel--full-screen" : ""}`}
                style={{ width, display: collapsed ? "none" : undefined }}
            >
                {!fullScreen && <div className="qa-resize-handle" onMouseDown={handleMouseDown} />}
                <header className="qa-chat-panel-header">
                    <span className="qa-chat-panel-title">
                        <svg className="qa-chat-panel-title-icon" viewBox="0 0 48 48" fill="none" width="22" height="22">
                            <path d="M24 6 C26 16, 32 22, 42 24 C32 26, 26 32, 24 42 C22 32, 16 26, 6 24 C16 22, 22 16, 24 6Z" fill="none" stroke="url(#qa-sparkle-gradient-header)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                            <defs><linearGradient id="qa-sparkle-gradient-header" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style={{stopColor: "#7c3aed"}}/><stop offset="100%" style={{stopColor: "#e84393"}}/></linearGradient></defs>
                        </svg>
                        {agentName}
                    </span>
                    <div className="qa-chat-panel-actions">
                        <button
                            className="qa-close-btn qa-full-screen-btn"
                            onClick={() => setFullScreen(!fullScreen)}
                            aria-pressed={fullScreen}
                            aria-label={fullScreen ? "Exit full screen" : "Full screen"}
                            title={fullScreen ? "Exit full screen" : "Full screen"}
                        >
                            <Icon name={fullScreen ? "exit-full-screen" : "full-screen"} />
                        </button>
                        <button className="qa-close-btn" onClick={handleCollapse} aria-label="Close chat panel">✕</button>
                    </div>
                </header>
                <iframe
                    key="qa-chat-frame"
                    className="qa-chat-iframe"
                    title={`${agentName} Chat`}
                    style={isResizing ? { pointerEvents: "none" } : undefined}
                    src={iframeSrc}
                    allow={`clipboard-read ${agentOrigin}; clipboard-write ${agentOrigin}`}
                    width="100%"
                    height="100%"
                />
            </div>
        </>
    );
};
